import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Loader2, X, ExternalLink, Paperclip, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, fileUrl } from "@/lib/api";

export function ReceiptImage({ id, className = "", icon = false }) {
  const [failed, setFailed] = useState(false);
  if (failed && icon) {
    return <span className={`flex flex-col items-center justify-center rounded-lg border text-xs font-semibold text-slate-500 ${className}`} data-testid="file-icon"><FileText className="h-6 w-6" />PDF</span>;
  }
  if (failed) {
    return (
      <a href={fileUrl(id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-semibold text-[hsl(var(--primary))] underline">
        <Paperclip className="h-4 w-4" /> Open file
      </a>
    );
  }
  return <img src={fileUrl(id)} alt="Receipt" onError={() => setFailed(true)} className={`rounded-lg border object-contain ${className}`} />;
}

export function ReceiptField({ value, onChange, testid }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    pending += 1;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/files", fd);
      onChange(r.data.id);
      toast.success("Receipt attached");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      pending -= 1;
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <input ref={ref} type="file" accept="image/*,application/pdf" className="hidden" data-testid={`${testid}-input`} onChange={(e) => upload(e.target.files?.[0])} />
      {value ? (
        <div className="flex items-start gap-3">
          <ReceiptImage id={value} className="h-28 w-28 bg-slate-50" />
          <Button type="button" variant="outline" size="sm" onClick={() => onChange(null)} data-testid={`${testid}-remove`}>
            <X className="mr-1 h-4 w-4" /> Remove
          </Button>
        </div>
      ) : (
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testid}-button`}
          className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-[hsl(var(--sand))] px-4 py-5 text-sm font-semibold text-slate-600 transition-colors hover:border-[hsl(var(--primary))]">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />}
          {busy ? "Uploading..." : "Tap to attach a receipt picture"}
        </button>
      )}
    </div>
  );
}

let pending = 0;
export const stillUploading = () => {
  if (pending > 0) { toast.error("Please wait until the file finishes uploading, then save."); return true; }
  return false;
};

export function ReceiptsField({ value, onChange, testid }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const ids = value || [];
  const upload = async (files) => {
    if (!files?.length) return;
    const room = 20 - ids.length;
    if (files.length > room) { toast.error(`You can attach up to 20 files. Only the first ${room} will be added.`); files = files.slice(0, room); }
    if (!files.length) return;
    setBusy(true);
    pending += 1;
    const added = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        added.push((await api.post("/files", fd)).data.id);
      } catch (e) { toast.error(`${file.name}: ${errMsg(e)}`); }
    }
    pending -= 1;
    if (added.length) { onChange([...ids, ...added].slice(0, 20)); toast.success(`${added.length} file(s) attached`); }
    setBusy(false);
    if (ref.current) ref.current.value = "";
  };
  return (
    <div className="space-y-2" data-testid={`${testid}-list`}>
      <input ref={ref} type="file" multiple accept="image/*,application/pdf" className="hidden" data-testid={`${testid}-input`} onChange={(e) => upload([...(e.target.files || [])])} />
      {ids.map((id, i) => (
        <div key={id} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-2" data-testid={`${testid}-file-${i}`}>
          <ReceiptImage id={id} icon className="h-14 w-14 bg-slate-50" />
          <span className="flex-1 text-sm font-semibold text-slate-700">File {i + 1}</span>
          <a href={fileUrl(id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-semibold text-[hsl(var(--primary))]" data-testid={`${testid}-open-${i}`}><ExternalLink className="h-4 w-4" /> Open</a>
          <Button type="button" variant="outline" size="sm" onClick={() => onChange(ids.filter((x) => x !== id))} data-testid={`${testid}-remove-${i}`}><X className="mr-1 h-4 w-4" /> Remove</Button>
        </div>
      ))}
      {ids.length < 20 && (
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testid}-button`}
          className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-[hsl(var(--sand))] px-4 py-4 text-sm font-semibold text-slate-600 transition-colors hover:border-[hsl(var(--primary))]">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />}
          {busy ? "Uploading..." : ids.length ? "Attach more files (photos or PDFs)" : "Tap to attach files (photos or PDFs, several allowed)"}
        </button>
      )}
    </div>
  );
}

export function ReceiptViewer({ id, onClose }) {
  const ids = Array.isArray(id) ? id : id ? [id] : [];
  return (
    <Dialog open={ids.length > 0} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="receipt-viewer">
        <DialogHeader><DialogTitle>{ids.length > 1 ? `Attached files (${ids.length})` : "Receipt"}</DialogTitle></DialogHeader>
        {ids.map((f, i) => (
          <div key={f} className="space-y-1" data-testid={`receipt-viewer-file-${i}`}>
            {ids.length > 1 && <p className="text-sm font-semibold text-slate-700">File {i + 1}</p>}
            <ReceiptImage id={f} icon className="max-h-[70vh] min-h-24 w-full" />
            <a href={fileUrl(f)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-semibold text-[hsl(var(--primary))]" data-testid={`receipt-viewer-open-${i}`}>
              <ExternalLink className="h-4 w-4" /> Open in new tab
            </a>
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
}
